/**
 * Вкладка «Медиа» окна товара: лента визуалов по кабинетам (вкладки кабинетов,
 * порядок, замена, удаление) для ВСЕХ товаров и превью макета отдельным блоком
 * (это файл печати, на площадки он не уходит). У товара с макетом правка набора
 * идёт с предупреждением: перегенерация воронки заменит ручной порядок.
 */

import React, { useState } from 'react'
import { ImageOff, Info, Loader2 } from 'lucide-react'
import { AuthorizedMediaImage } from '../../../../shared/media/authorizedMedia'
import { ProductMediaManager } from '../ProductMediaManager'
import type { ProductV2 } from '../../types'
import { isManualProduct, themeNumberOfProduct } from './model'
import { SectionTitle } from './parts'

export interface GalleryItem {
  key: string
  src: string
  label: string
  alt: string
}

interface MediaTabProps {
  product: ProductV2
  productPhotos: GalleryItem[]
  layoutPreviews: GalleryItem[]
  onOpenImage: (index: number) => void
  onChanged: () => void
  /** «Переотправить фото…» — после сохранения набора, записанного в журнал. */
  onResendPhotos?: () => void
}

const LEGACY_NOTICE =
  'Визуалы товара не разложены по кабинетам ПИМ (пришли из старого источника или с площадки). ' +
  'Показаны как есть; порядок по кабинетам появится после генерации визуалов или ручной загрузки в кабинет.'

const Thumb: React.FC<{ item: GalleryItem; aspect: string; frame: string; onOpen: () => void }> = ({
  item,
  aspect,
  frame,
  onOpen,
}) => (
  <button
    type="button"
    onClick={onOpen}
    title={item.label}
    className={`flex ${aspect} items-center justify-center overflow-hidden rounded-md ${frame} bg-gray-50 dark:bg-zinc-800`}
  >
    <AuthorizedMediaImage
      src={item.src}
      alt={item.alt}
      loading="lazy"
      className="h-full w-full object-contain"
      loadingFallback={<Loader2 aria-label="Загрузка фото" className="h-5 w-5 animate-spin text-gray-400" />}
      errorFallback={<ImageOff aria-label="Фото недоступно" className="h-7 w-7 text-gray-300 dark:text-zinc-600" />}
    />
  </button>
)

export const MediaTab: React.FC<MediaTabProps> = ({
  product,
  productPhotos,
  layoutPreviews,
  onOpenImage,
  onChanged,
  onResendPhotos,
}) => {
  const urls = product.image_urls ?? []
  /** null — менеджер ещё не ответил; 0 — визуалов кабинетов нет вовсе. */
  const [cabinets, setCabinets] = useState<number | null>(null)
  // Лента кабинетов — основной вид. Простая сетка фото товара нужна лишь там, где
  // в кабинетах визуалов нет, а адреса на товаре есть (старые товары без product_artifacts).
  const fallbackGrid = cabinets === 0 && productPhotos.length > 0
  const nothing = cabinets === 0 && productPhotos.length === 0 && layoutPreviews.length === 0
  return (
    <div className="flex flex-col">
      <div className={fallbackGrid ? 'order-2 mt-4' : undefined}>
        <ProductMediaManager
          article={product.article}
          origin={product.design?.origin}
          themeNumber={themeNumberOfProduct(product)}
          onChanged={onChanged}
          onResendPhotos={onResendPhotos}
          onLoaded={(summary) => setCabinets(summary.cabinets)}
          collapseWhenEmpty={productPhotos.length > 0}
        />
      </div>
      {nothing && (
        <p className="py-4 text-center text-sm text-gray-500 dark:text-zinc-400">Изображения товара не заданы</p>
      )}
      {fallbackGrid && (
        <section data-testid="legacy-photos-block" className="order-1">
          <div className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 dark:text-zinc-500" />
            <p>{LEGACY_NOTICE}</p>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {productPhotos.map((item, index) => (
              <Thumb
                key={item.key}
                item={item}
                aspect="aspect-[210/297]"
                frame="border border-gray-200 dark:border-zinc-800"
                onOpen={() => onOpenImage(index)}
              />
            ))}
          </div>
        </section>
      )}
      {layoutPreviews.length > 0 && (
        <section data-testid="layout-preview-block" className="order-3 mt-6">
          <SectionTitle>Макет</SectionTitle>
          <p className="mb-2 text-xs text-gray-500 dark:text-zinc-400">
            Файл для печати, на площадки не выгружается
          </p>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-6">
            {layoutPreviews.map((item, index) => (
              <Thumb
                key={item.key}
                item={item}
                aspect="aspect-square"
                frame="border border-dashed border-gray-300 dark:border-zinc-700"
                onOpen={() => onOpenImage(productPhotos.length + index)}
              />
            ))}
          </div>
        </section>
      )}

      {/* Адреса — набор карточки товара (сайт): служебная справка, свёрнута. */}
      {!isManualProduct(product) && urls.length > 0 && (
        <details className="order-4 mt-4 text-xs text-gray-600 dark:text-zinc-300">
          <summary className="cursor-pointer select-none text-gray-500 hover:text-gray-800 dark:text-zinc-400 dark:hover:text-zinc-100">
            Показать адреса
          </summary>
          <ul className="mt-1.5 space-y-1">
            {urls.map((url) => (
              <li key={url} className="truncate font-mono text-2xs">
                {url}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
